import { PropsWithChildren } from 'react'

/**
 * Shared props interface for all icon components.
 * Ensures consistency across icon implementations.
 */
export interface IconProps {
    /**
     * Size of the icon in pixels or as a string (default: 24)
     */
    size?: number | string
    /**
     * Explicit width of the icon. When provided, overrides `size` for width.
     */
    width?: number | string
    /**
     * Explicit height of the icon. When provided, overrides `size` for height.
     */
    height?: number | string
    /**
     * Color of the icon. If not provided, uses currentColor to inherit text color.
     */
    color?: string
    /**
     * Custom viewBox (default: "0 0 24 24").
     * Useful for icons that are not 24x24.
     */
    viewBox?: string
    /**
     * SVG preserveAspectRatio attribute (optional).
     */
    preserveAspectRatio?: string
    /**
     * Additional CSS classes
     */
    className?: string
    /**
     * Accessible label for the icon. When provided, the icon will be treated as meaningful
     * (with aria-label). When not provided, the icon will be treated as decorative
     * (aria-hidden="true").
     */
    ariaLabel?: string
}

/**
 * Base wrapper component for all icon SVGs.
 * Handles common SVG structure, accessibility attributes, and styling.
 * Reduces code duplication across icon components.
 */
const BaseSvgIcon = ({
    size = 24,
    width,
    height,
    color,
    viewBox = '0 0 24 24',
    preserveAspectRatio,
    className,
    ariaLabel,
    children,
}: PropsWithChildren<IconProps>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height={height ?? size}
            width={width ?? size}
            viewBox={viewBox}
            fill={color || 'currentColor'}
            className={className}
            preserveAspectRatio={preserveAspectRatio}
            aria-hidden={ariaLabel ? undefined : true}
            aria-label={ariaLabel}>
            {children}
        </svg>
    )
}

export default BaseSvgIcon
